
import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

// Client website data
const clientWebsites = [
  {
    id: 1,
    category: 'security',
    name: 'SecureShield',
    description: 'Home security solutions with 24/7 monitoring',
    image: 'https://images.unsplash.com/photo-1558002038-1055907df827?q=80&w=1000',
    quote: 'Our leads increased by 72% in the first month!',
    client: 'Marcus J.',
  },
  {
    id: 2,
    category: 'cafe',
    name: 'Morning Brew',
    description: 'Specialty coffee shop with online ordering',
    image: 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?q=80&w=1000',
    quote: "Didn't expect it to look this clean!",
    client: 'Sarah L.',
  },
  {
    id: 3,
    category: 'salon',
    name: 'Elegance Hair',
    description: 'Premier hair salon with online booking',
    image: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?q=80&w=1000',
    quote: 'Bookings up 45% since launch!',
    client: 'Emma R.',
  },
  {
    id: 4,
    category: 'fitness',
    name: 'PowerFit',
    description: 'Personal training and fitness center',
    image: 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?q=80&w=1000',
    quote: 'The site converted better than our old one immediately.',
    client: 'Jake T.',
  },
  {
    id: 5,
    category: 'retail',
    name: 'Urban Threads',
    description: 'Boutique clothing store with e-commerce',
    image: 'https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?q=80&w=1000',
    quote: 'Our sales doubled in the first quarter!',
    client: 'Olivia P.',
  },
];

const PortfolioSection = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  
  // Filter websites based on active category
  const filteredWebsites = activeCategory === 'all' 
    ? clientWebsites 
    : clientWebsites.filter(site => site.category === activeCategory);

  // Auto scroll effect
  useEffect(() => {
    const interval = setInterval(() => {
      if (filteredWebsites.length <= 1) return;
      setCurrentIndex((prev) => (prev + 1) % filteredWebsites.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [filteredWebsites.length]);

  // Handle navigation
  const handlePrev = () => {
    if (filteredWebsites.length <= 1) return;
    setCurrentIndex((prev) => (prev - 1 + filteredWebsites.length) % filteredWebsites.length);
  };

  const handleNext = () => {
    if (filteredWebsites.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % filteredWebsites.length);
  };

  return (
    <section id="portfolio" className="bg-knightblack py-20 text-softwhite">
      <div className="section-container">
        <h2 className="section-title text-softwhite">
          These Small Businesses All Started With a <span className="text-goldcore">DM</span>
        </h2>
        
        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-12">
          {['all', 'security', 'cafe', 'salon', 'fitness', 'retail'].map((category) => (
            <button
              key={category}
              onClick={() => {
                setActiveCategory(category);
                setCurrentIndex(0);
              }}
              className={`px-4 py-2 rounded-full transition-all ${
                activeCategory === category
                  ? 'bg-goldcore text-knightblack font-semibold'
                  : 'bg-deepshadow/30 text-softwhite hover:bg-deepshadow/50'
              }`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>
        
        {/* Portfolio Carousel */}
        <div className="relative">
          {/* Navigation Arrows */}
          <button 
            onClick={handlePrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-knightblack/70 p-2 rounded-full text-goldcore hover:bg-knightblack transition-colors"
            aria-label="Previous project"
          >
            <ChevronLeft size={24} />
          </button>
          
          <button 
            onClick={handleNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-knightblack/70 p-2 rounded-full text-goldcore hover:bg-knightblack transition-colors"
            aria-label="Next project"
          >
            <ChevronRight size={24} />
          </button>
          
          {/* Carousel Container */}
          <div 
            ref={carouselRef}
            className="overflow-hidden rounded-xl"
          >
            <div 
              className="flex transition-transform duration-500 ease-in-out h-[500px]"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {filteredWebsites.map((website, index) => (
                <div 
                  key={website.id} 
                  className="w-full flex-shrink-0 relative"
                >
                  {/* Background Image */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${website.image})` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-knightblack via-knightblack/70 to-transparent"></div>
                  </div>
                  
                  {/* Content */}
                  <div className="relative z-10 h-full flex flex-col justify-end p-8 md:p-12">
                    <div className="mb-4">
                      <span className="inline-block px-3 py-1 rounded-full text-xs bg-goldcore text-knightblack font-medium mb-4">
                        {website.category.charAt(0).toUpperCase() + website.category.slice(1)}
                      </span>
                      <h3 className="text-3xl font-bold text-softwhite mb-2">{website.name}</h3>
                      <p className="text-softwhite/80 mb-6">{website.description}</p>
                    </div>
                    
                    {/* Quote Overlay */}
                    <div className="bg-knightblack/70 backdrop-blur-sm rounded-lg p-4 border-l-4 border-goldcore max-w-md">
                      <p className="italic text-softwhite/90 mb-2">"{website.quote}"</p>
                      <p className="text-goldcore font-medium text-sm">— {website.client}</p>
                    </div>
                    
                    {/* View Case Study Link */}
                    <div className="absolute bottom-6 right-6">
                      <a href="#" className="flex items-center text-goldcore hover:underline">
                        <span className="mr-1">View Case Study</span>
                        <ExternalLink size={16} />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Carousel Indicators */}
          <div className="flex justify-center mt-6">
            {filteredWebsites.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full mx-1 ${
                  currentIndex === index ? 'bg-goldcore' : 'bg-deepshadow/50 hover:bg-deepshadow/70'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
